import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { SseService } from '@ortho-next/sse';
import { BehaviorSubject, filter, finalize, first, map, Observable, of, Subscription, switchMap, take, takeWhile, tap, throwError, timer } from 'rxjs';
import { APP_CONFIG, DeviceEnum, ExpressionOperator, LanguageService, LoaderService, MountingParameters, PAGE, PatientBase, PatientRepository, Plan, PlanStepEnum, ReferenceTypeEnum, SearchFields, ToastService } from '../core';
import { Attachment, IPlanControl, PlanControl, PlanForm, planIdNotUnique, setPlanValidators } from '../models';
import { ConfirmationComponent } from '../shared';
import { AttachmentService } from './attachment.service';
import { PatientService } from './patient.service';
import { PlanService } from './plan.service';
import { TlhexService } from './tlhex.service';
import { UserService } from './user.service';


/**
 * This service handles plan form page.
 */
@Injectable({ providedIn: 'root' })
export class PlanFormService {

	private readonly router = inject(Router);
	private readonly sse = inject(SseService);
	private readonly config = inject(APP_CONFIG);
	private readonly modalSrv = inject(NgbModal);
	private readonly userSrv = inject(UserService);
	private readonly planSrv = inject(PlanService);
	private readonly toastSrv = inject(ToastService);
	private readonly tlhexSrv = inject(TlhexService);
	private readonly langSrv = inject(LanguageService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly patientSrv = inject(PatientService);
	private readonly patRepo = inject(PatientRepository);
	private readonly attachSrv = inject(AttachmentService);

	private readonly _planForm = new BehaviorSubject<FormGroup<IPlanControl> | null>(null);

	activeTab: 1 | 2 = 1;
	isEdit = false;
	private _patient: PatientBase | null = null;
	private _plan: Plan | null = null;
	private _userId: string = '';

	private _isSaving = false;
	private _isReadonly = false;
	private _startAnalysis = false;
	private _resetKey: string | null = null;
	private _activatedRoute?: ActivatedRoute;

	private _apImage$?: Subscription;
	private _ltImage$?: Subscription;
	private readonly _labels = this.langSrv.labels.asReadonly();

	private _checkupRings: MountingParameters | null = null;


	// #region getter
	private get _formValue(): PlanForm { return this._planForm?.value?.value as PlanForm; };

	private get _formCtrl(): IPlanControl | undefined {
		return this._planForm?.value?.controls;
	}
	get isFormDirty(): boolean { return !!this._planForm?.value?.dirty; }

	get patient(): PatientBase | null { return this._patient; }
	get userId(): string { return this._userId; }
	get plan(): Plan | null { return this._plan; }
	get checkRings(): MountingParameters | null { return this._checkupRings; }

	/**
	 * Check if plan data tab is success. 
	 */
	get isDataTabSuccess(): boolean {
		return this.activeTab !== 1 && this.isDataValid;
	}
	/**
	 * Check if plan setup tab is success. 
	 */
	get isSetupTabSuccess(): boolean {
		return !!this._plan && this.activeTab === 2 && this.isFormValid;
	}
	/**
	 * Check if plan analysis tab is enabled. 
	 */
	get isAnalysisTabEnabled(): boolean {
		return this.activeTab == 2;
	}
	/**
	 * Check if tab next button is enabled. 
	 */
	get isTabNextEnabled(): boolean {
		return !this.modalSrv.hasOpenModals() && ((this.activeTab === 1 && this.isDataValid) || (this.activeTab === 2 && !this.isStartAnalysisDisabled));
	}
	/**
	 * Check if plan data are valid.
	 */
	get isDataValid(): boolean {
		return !!this._formCtrl
			&& this._formCtrl.planId.valid
			&& this._formCtrl.notes.valid
			&& this._formCtrl.device.valid
			&& this._formCtrl.postOperative.valid
			&& this._formCtrl.planType.valid
			&& this._formCtrl.referenceType.valid;
	}
	/**
	 * Check if form is valid.
	 */
	get isFormValid(): boolean { return !!this._planForm?.value?.valid; }
	/**
	 * Check if image section is disabled.
	 */
	get isImagesBoxDisabled(): boolean { return !!this._formCtrl?.anatomicalSite.invalid; }
	/**
	 * Get form reference type.
	 */
	get referenceType(): ReferenceTypeEnum { return this._formValue?.referenceType; }
	/**
	 * Check if plan is readonly.
	 */
	get isReadonly(): boolean {
		return this._isReadonly;
	}
	/**
	 * Check if save button is visible.
	 */
	get isSaveVisible(): boolean {
		return this.activeTab === 2;
	}
	/**
	 * Check if save button is disabled.
	 */
	get isSaveDisabled(): boolean {
		return this._isSaving || this._isReadonly;
	}
	/**
	 * Check is start analysis button is disabled.
	 */
	get isStartAnalysisDisabled(): boolean {
		return !this.isFormValid || this._isSaving || this.attachSrv.apLoadingStatus || this.attachSrv.ltLoadingStatus;
	}
	/**
	 * Check if reset analysis warning is visible.
	 */
	get isResetAnalysisWarning(): boolean {
		return this._plan != null && this._hasToResetAnalysis;
	}
	get isRingCalibrationInvalid(): boolean {
		return (
			(!!this._formValue?.apImage && this._isRingCalibInvalid(this._formValue.apImage, this._formValue.referenceType, this._formValue.postOperative))
			|| (!!this._formValue?.ltImage && this._isRingCalibInvalid(this._formValue.ltImage, this._formValue.referenceType, this._formValue.postOperative))
		);
	}
	private _isRingCalibInvalid(image: Attachment, refType: ReferenceTypeEnum, isPostop: boolean): boolean {
		return (!!image.ringProximalId || !!image.ringDistalId) && (image.referenceType !== refType || !isPostop);
	}

	getForm(): Observable<FormGroup<IPlanControl>> { return this._planForm.asObservable().pipe(first(form => !!form)); };

	// #endregion

	// #region init 

	/**
	* Navigate to patient list page.
	*/
	returnToPatientList(): void {
		this.router.navigateByUrl(`/${PAGE.LIST}`);
	}

	/**
	 * Init plan form page.
	 */
	initForm(activatedRoute: ActivatedRoute): Observable<boolean> {
		this._activatedRoute = activatedRoute;
		const route = activatedRoute.snapshot;
		const path = route.routeConfig?.path;
		if (path === PAGE.PLAN_NEW) {
			this.isEdit = false;
			this.activeTab = 1;
			const patientGuid = route.queryParams['patientGuid'];
			if (!patientGuid) throw new Error('Patient Guid is required');
			return this._initCreateForm(patientGuid);
		} else if (path === PAGE.PLAN_EDIT) {
			this.isEdit = true;
			this.activeTab = 2;
			const planGuid = route.queryParams['planGuid'];
			if (!planGuid) throw new Error('Plan Guid is required');
			const checkupFirstAccess = (route.queryParams['checkupFirstAccess'] as string)?.toLowerCase() === 'true';
			return this._initEditForm(planGuid, checkupFirstAccess);
		} else {
			return throwError(() => new Error('Plan form page loading error'));
		}
	}

	private _initCreateForm(patientGuid: string): Observable<boolean> {
		this.loaderSrv.show();
		return this.patientSrv.getPatient(patientGuid).pipe(
			filter(pat => !!pat),
			tap(pat => {
				this._patient = pat;
				this._userId = pat.userGuid;
				this._initForm(this._patient.id, null, null, null, false);
				this.initImages(this._patient.id, this._userId, null, null);
			}),
			map(() => true),
			finalize(() => this.loaderSrv.hide())
		);
	}

	private _initEditForm(planGuid: string, checkupFirstAccess: boolean): Observable<boolean> {
		this.loaderSrv.show();
		return this.planSrv.getPlan(planGuid).pipe(
			switchMap(plan => this._isPlanInvalid(plan) ? this._handleInitEditFormInvalid() : this._handleInitEditFormValid(plan, checkupFirstAccess)),
		);
	}

	private _handleInitEditFormInvalid(): Observable<boolean> {
		this.loaderSrv.hide();
		this._openInvalidPlanModal();
		return of(false);
	}

	private _handleInitEditFormValid(plan: Plan, checkupFirstAccess: boolean): Observable<boolean> {
		let _isCheckupFirstAccess = false;
		return of(plan).pipe(
			tap(plan => {
				this._plan = plan;
				this._patient = plan.patient;
				this._userId = plan.userGuid;
				// force restart from tab data for checkup/preop2postop first access.
				_isCheckupFirstAccess = (!!plan.isCheckUp || !!plan.preOp2PostOp) && checkupFirstAccess;
				_isCheckupFirstAccess && (this.activeTab = 1);
			}),
			switchMap(plan => this.userSrv.isCustomerCareOtherUser(this._plan!.userGuid).pipe(
				tap(isReadonly => this._isReadonly = isReadonly),
				map(() => plan)
			)),
			switchMap(plan => (plan.apImageGuid || plan.ltImageGuid) ?
				this.attachSrv.loadImages(this._patient!.id, this._plan!.userGuid, plan.apImageGuid, plan.ltImageGuid) :
				of([null, null] as [Attachment | null, Attachment | null])
			),
			tap((images: [ap: Attachment | null, lt: Attachment | null]) => {
				this._initForm(this._patient!.id, this._plan, images[0], images[1], _isCheckupFirstAccess);
				this.initImages(this._patient!.id, this._plan!.userGuid, images[0], images[1]);
			}),
			switchMap(() => this._plan?.isCheckUp ? this.tlhexSrv.getMountingParameters(this._plan.id, this._plan.userGuid) : of(null)),
			tap(mouParams => this._checkupRings = mouParams),
			map(() => true),
			finalize(() => this.loaderSrv.hide())
		);
	}

	/**
	 * Check if plan is invalid.
	 */
	private _isPlanInvalid(plan: Plan | null): boolean {
		return !plan || !!plan.isPublished || !!plan.isRevoked;
	}

	private _initForm(patientId: string, plan: Plan | null, apImage: Attachment | null, ltImage: Attachment | null, isCheckupFirstAccess: boolean): void {
		const form = new FormGroup<IPlanControl>(new PlanControl());
		setPlanValidators(form);
		if (plan) {
			const forceWithImages = isCheckupFirstAccess;
			form.patchValue(this.planSrv.createPlanForm(plan, apImage, ltImage, forceWithImages));
		}
		form.controls.patientGuid.setValue(patientId);
		this._planForm.next(form);
	}

	private _openInvalidPlanModal(): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this._labels().INVALID_PLAN_MODAL_TITLE,
			message: this._labels().INVALID_PLAN_MODAL_MESSAGE,
			confirmBtn: this._labels().INVALID_PLAN_MODAL_CONFIRM_BTN,
			isCancelBtn: false
		};
		modalRef.result.then(() => this.returnToPatientList());
	}

	private initImages(patientId: string, userId: string, apImg: Attachment | null, ltImg: Attachment | null): void {
		this.attachSrv.setPlanImages(patientId, userId, apImg, ltImg);
		this._apImage$ = this.attachSrv.loadApImage().subscribe(image => this._updateApImage(image));
		this._ltImage$ = this.attachSrv.loadLtImage().subscribe(image => this._updateLtImage(image));
	}

	/**
	* Check if plan id already exists.
	*/
	private checkPlanIdAlreadyExists(): Observable<boolean> {
		this.loaderSrv.show();
		const filters = [
			{ name: SearchFields.userGuid, operator: ExpressionOperator.Eq, value: this._userId },
			{ name: SearchFields.patGuid, operator: ExpressionOperator.Eq, value: this._patient?.id },
			{ name: SearchFields.planNumber, operator: ExpressionOperator.Eq, value: this._formCtrl?.planId.value }
		];
		if (this._plan) {
			filters.push({ name: SearchFields.planGuid, operator: ExpressionOperator.Ne, value: this._plan?.id });
		}
		return this.patRepo.search({
			top: 0,
			page: 0,
			patientOnly: false,
			deleted: false,
			sortOrder: null,
			filters: filters,
		}).pipe(
			map(res => res.result),
			filter(res => !!res),
			map(res => !!res.count),
			tap(alreadyExists => {
				if (alreadyExists) {
					this.toastSrv.showError(this._labels().PLAN_FORM_COMPONENT_ERROR_MESSAGE);
					this._formCtrl?.planId.setErrors({ [`${planIdNotUnique}`]: null });
				}
			}),
			finalize(() => this.loaderSrv.hide()),
		);
	}

	// #endregion

	// #region update

	private _initializedAP = false;
	private _updateApImage(image: Attachment | null): void {
		this._formCtrl?.apImage.setValue(image);
		if (this._initializedAP) this._formCtrl?.apImage.markAsDirty();
		this._initializedAP = true;
	}

	private _initializedLT = false;
	private _updateLtImage(image: Attachment | null): void {
		this._formCtrl?.ltImage.setValue(image);
		if (this._initializedLT) this._formCtrl?.ltImage.markAsDirty();
		this._initializedLT = true;
	}

	// #endregion

	// #region save

	/**
	 * Navigate to plan form next tab.
	 */
	nextTab(ev: NgbNavChangeEvent): void {
		if (ev.activeId === 1) {
			ev.preventDefault();
			this.isTabNextEnabled ? this._handlePlanIdCheck() : this.validatePlanForm(true);
		} else if (ev.activeId === 2 && ev.nextId === 3) {
			ev.preventDefault();
			this.isTabNextEnabled ? this.savePlan(true) : this.validatePlanForm();
		} else if (ev.activeId === 2 && ev.nextId === 1) {
			ev.preventDefault();
			this._backSetupTab();
		}
	}

	private _handlePlanIdCheck(): void {
		if (this.plan?.number === this._formCtrl?.planId.value) {
			this._toSetupTab();
		} else {
			this.checkPlanIdAlreadyExists().subscribe(alreadyExists => {
				if (alreadyExists) {
					this.activeTab = 1;
				} else {
					this._toSetupTab();
				}
			});
		}
	}

	private _toSetupTab(): void {
		if (this.isRingCalibrationInvalid) this._resetImages();
		this.activeTab = 2;
		document.body.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	private _backSetupTab(): void {
		this.activeTab = 1;
		document.body.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	private _resetImages(): void {
		this.attachSrv.setApImageId(null, false);
		this.attachSrv.setLtImageId(null, false);
	}

	private validatePlanForm(onlyData = false): void {
		const props: (keyof PlanControl)[] = onlyData ?
			['planId', 'notes', 'device', 'postOperative', 'planType', 'referenceType'] :
			Object.keys(new PlanControl()) as (keyof PlanControl)[];
		for (const prop of props) {
			this._planForm.value?.controls[prop].markAsTouched();
			this._planForm.value?.controls[prop].updateValueAndValidity();
		}
		this.toastSrv.showError(this.langSrv.labels().PLAN_FORM_COMPONENT_ERROR_MESSAGE);
	}

	private get _isDataChanged(): boolean {
		if (!this._formValue) return false;
		const isDeviceChanged = this._formValue?.device != this._plan!.device;
		const isScenarioChanged = this._formValue?.postOperative != this._plan!.isPostOperative;
		const isTypeChanged = this._formValue?.planType != this._plan!.type;
		const isReferenceChanged = this._formValue?.referenceType != this._plan!.referenceType;
		return isDeviceChanged || isScenarioChanged || isTypeChanged || isReferenceChanged;
	}

	private get _hasToResetAnalysis(): boolean {
		if (!this._formValue || (!!this._plan?.isCheckUp && (!this._plan.step || this._plan.step === PlanStepEnum.None) && !this._plan.modifiedDate)) return false;
		const isApChanged = this._formValue?.apImage?.id != this._plan!.apImageGuid;
		const isLtChanged = this._formValue?.ltImage?.id != this._plan!.ltImageGuid;
		const isSiteChanged = this._plan!.side != null && (this._formValue?.side != this._plan!.side || this._formValue?.boneType != this._plan!.boneType);
		const isBoneTypeChanged = this._formValue?.boneType != this._plan!.boneType;
		const isWithImagesChanged = (!this._formValue?.withImages && (!!this._plan?.apImageGuid || !!this._plan?.ltImageGuid)) || (!!this._formValue?.withImages && (!this._plan?.apImageGuid && !this._plan?.ltImageGuid));
		return !this.attachSrv.planImageAlreadyReset && (this._isDataChanged || isApChanged || isLtChanged || isSiteChanged || isBoneTypeChanged || isWithImagesChanged);
	}

	_openResetAnalysisModal(confirmFunction: Function): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this._labels().PLAN_FORM_COMPONENT_RESET_ANALYSIS_MODAL_TITLE,
			message: this._labels().PLAN_FORM_COMPONENT_RESET_ANALYSIS_MODAL_MESSAGE,
			cancelBtn: this._labels().PLAN_FORM_COMPONENT_RESET_ANALYSIS_MODAL_NO,
			confirmBtn: this._labels().PLAN_FORM_COMPONENT_RESET_ANALYSIS_MODAL_YES
		};
		modalRef.result.then(() => confirmFunction.call(this)).catch(error => false);
	}

	/**
	* Open modal to confirm analysis reset.
	*/
	savePlan(startAnalysis: boolean): void {
		if (this._planForm?.value?.valid) {
			if (this._plan != null && this._hasToResetAnalysis) {
				this._openResetAnalysisModal(() => this._savePlan(startAnalysis))
			} else {
				this._savePlan(startAnalysis);
			}
		} else {
			this.validatePlanForm();
		}
	}

	private _savePlan(startAnalysis: boolean): void {
		this._startAnalysis = startAnalysis;
		this.loaderSrv.show();
		if (this.isEdit) { // Update Plan
			if (this._isReadonly && this._startAnalysis) {
				this._navigateToAnalysis(this._plan!, false);
			} else {
				const hasToResetAnalysis = this._hasToResetAnalysis;
				this._isSaving = true;
				if (hasToResetAnalysis) {
					this._handleResetEvents(this._formValue);
				}
				this.planSrv.updatePlan(this._plan!, this._formValue).subscribe({
					next: plan => {
						// update form
						this._plan = plan;
						this._updateForm(plan);
						this.attachSrv.setPlanImages(this._patient!.id, this._plan.userGuid, this._formValue.apImage ?? null, this._formValue.ltImage ?? null);

						if (!hasToResetAnalysis) {
							this._startAnalysis && this._navigateToAnalysis(this._plan, this.attachSrv.planImageAlreadyReset);

							this.toastSrv.showSuccess(this._labels().PLAN_FORM_COMPONENT_UPDATE_MESSAGE);
							this.loaderSrv.hide();
							this._isSaving = false;
							this._startAnalysis = false;
						}
					},
					error: (err: Error) => {
						this.toastSrv.showError(this._labels().PLAN_FORM_COMPONENT_ERROR_MESSAGE);
						this._handlePlanIdNotUnique(err);
						this._clearResetEvents();
						this.loaderSrv.hide();
						this._isSaving = false;
						this._startAnalysis = false;
					}
				});
			}
		} else { // New Plan
			this._isSaving = true;
			this.planSrv.savePlanForm(this._formValue).pipe(
				finalize(() => { this._isSaving = false; this.loaderSrv.hide(); })
			).subscribe({
				next: (plan: Plan) => {
					this.toastSrv.showSuccess(this._labels().PLAN_FORM_COMPONENT_SAVE_MESSAGE);

					// update form
					this._plan = plan;
					this._updateForm(plan);
					this.attachSrv.setPlanImages(this._patient!.id, this._plan.userGuid, this._formValue.apImage ?? null, this._formValue.ltImage ?? null);

					// update url and/or navigate
					this._updateQueryParams(plan.id);
					if (this._startAnalysis) this._navigateToAnalysis(plan, false);
					this._startAnalysis = false;
				},
				error: (err: Error) => {
					this.toastSrv.showError(this._labels().PLAN_FORM_COMPONENT_ERROR_MESSAGE);
					this._handlePlanIdNotUnique(err);
					this._startAnalysis = false;
				}
			});
		}
	}

	private _updateForm(plan: Plan): void {
		const apImage = plan.apImageGuid ? this._formValue.apImage : null;
		const ltImage = plan.ltImageGuid ? this._formValue.ltImage : null;
		const planForm = this.planSrv.createPlanForm(plan, apImage ?? null, ltImage ?? null, false);
		this._planForm.value!.patchValue(planForm);
	}

	/**
	 * Handle subscription to analysis reset event.
	 */
	private _handleResetEvents(planForm: PlanForm): void {
		if (planForm.device === DeviceEnum.Tlhex) {
			this._resetKey = `OrthoNext.Tlhex.Reset.${this._plan!.id}`;
		} else {
			this._resetKey = `OrthoNext.Ray.Reset.${this._plan!.id}`;
		}
		console.log('Resetting... ' + this._resetKey);
		this.sse.subscribe(this._resetKey, (success: boolean) => {
			console.log('Resetted: ' + success);
			if (success) {
				this.toastSrv.showSuccess(this._labels().PLAN_FORM_COMPONENT_UPDATE_MESSAGE);

				// Force step on reset success for correct analysis redirecction.
				if (this._plan!.step === PlanStepEnum.RPM) this._plan!.step = PlanStepEnum.None;

				this._startAnalysis && this._navigateToAnalysis(this._plan!, true);
			} else {
				this.toastSrv.showError(this._labels().PLAN_FORM_COMPONENT_RESET_ERROR);
			}
			this._closeResetSubscription();
		});
		// timeout reset subscription
		timer(0, this.config.resetSubscriptionTimeout / 10).pipe(
			takeWhile(() => !!this._resetKey),
			take(10),
		).subscribe({
			complete: () => {
				if (this._resetKey) {
					console.log('Not Resetted');
					this.toastSrv.showError(this._labels().PLAN_FORM_COMPONENT_RESET_ERROR);
					this._closeResetSubscription();
				}
			}
		});
	}

	private _closeResetSubscription(): void {
		this._clearResetEvents();
		this.loaderSrv.hide();
		this._isSaving = false;
		this._startAnalysis = false;
	}

	private _clearResetEvents(): void {
		if (this._resetKey) {
			this.sse.unsubscribe(this._resetKey);
			this._resetKey = null;
		}
	}

	private _updateQueryParams(planId: string): void {
		this.router.navigate(
			[`/${PAGE.PLAN_EDIT}`],
			{
				relativeTo: this._activatedRoute,
				queryParams: { patientGuid: null, planGuid: planId },
				queryParamsHandling: 'merge'
			});
	}

	private _navigateToAnalysis(plan: Plan, hasToReset: boolean): void {
		let appRedirectUrl;
		if (plan.device === DeviceEnum.Tlhex) {
			appRedirectUrl = this.config.tlhexSite;
		} else if (hasToReset) { //new or renew plan
			appRedirectUrl = this.config.nextraySite;
		} else if (plan.step === PlanStepEnum.RPM) { //fitbone
			appRedirectUrl = this.config.fitboneSite;
		} else {
			appRedirectUrl = this.config.nextraySite;
		}
		globalThis.location.href = appRedirectUrl + '?caseGuid=' + plan.id;
	}

	private _handlePlanIdNotUnique(err: Error): void {
		if (err.message === 'PlanNumberNotUnique') {
			this._formCtrl?.planId.setErrors({ [`${planIdNotUnique}`]: null });
		}
	}

	// #endregion

	// #region reset

	/**
	 * Reset plan form page.
	 */
	reset(): void {
		this.isEdit = false;
		this._planForm.next(null);
		this._patient = null;
		this._plan = null;
		this._userId = '';

		this._startAnalysis = false;
		this._isSaving = false;
		this._resetKey = null;
		this._activatedRoute = undefined;

		this._initializedAP = false;
		this._apImage$?.unsubscribe();
		this._initializedLT = false;
		this._ltImage$?.unsubscribe();

		this._checkupRings = null;

		this.attachSrv.resetPlanImages();
		this._clearResetEvents();
	}

	// #endregion

}